import {
  CaretDown,
  CaretUp,
  FilmStrip,
  Image,
  MusicNotes,
  X,
} from '@phosphor-icons/react';
import { UploadFileItem } from '@reelplicate/ui';
import { useState } from 'react';

import { useMedia } from './media-context';

const ICONS = { clip: FilmStrip, image: Image, audio: MusicNotes } as const;

/**
 * Figma "Upload queue" (191:2767): pinned bottom-right while files upload,
 * then finish on the server. Collapses to its header; closing clears the
 * finished ones.
 */
export function UploadQueue() {
  const { uploads, cancelUpload, dismissUploads } = useMedia();
  const [collapsed, setCollapsed] = useState(false);
  if (uploads.length === 0) return null;

  const done = uploads.filter((u) => u.status === 'complete').length;
  const all = done === uploads.length;
  const Caret = collapsed ? CaretUp : CaretDown;

  return (
    <section
      aria-label="Uploads"
      className="bg-bg-surface border-border-subtle fixed right-6 bottom-6 z-30 flex w-[360px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-[14px] border shadow-[0_12px_32px_0_rgba(0,0,0,0.14)]"
    >
      <div className="bg-bg-inverse flex items-center gap-2 py-3 pr-3 pl-4">
        <h2 aria-live="polite" className="text-label-md text-text-inverse flex-1">
          {all
            ? `Uploaded ${uploads.length} ${uploads.length === 1 ? 'file' : 'files'}`
            : `Uploading ${uploads.length} ${uploads.length === 1 ? 'file' : 'files'} · ${done} done`}
        </h2>
        <button
          type="button"
          aria-label={collapsed ? 'Expand uploads' : 'Collapse uploads'}
          aria-expanded={!collapsed}
          onClick={() => setCollapsed(!collapsed)}
          className="text-text-inverse focus-visible:shadow-focus-ring rounded-xs outline-none"
        >
          <Caret aria-hidden className="size-4" />
        </button>
        <button
          type="button"
          aria-label="Close uploads"
          onClick={dismissUploads}
          className="text-text-inverse focus-visible:shadow-focus-ring rounded-xs outline-none"
        >
          <X aria-hidden className="size-4" />
        </button>
      </div>
      {!collapsed && (
        <>
          <ul className="flex max-h-[300px] flex-col gap-1 overflow-y-auto p-2">
            {uploads.map((u) => (
              <li key={u.id}>
                <UploadFileItem
                  name={u.name}
                  size={u.size}
                  icon={ICONS[u.kind as keyof typeof ICONS] ?? FilmStrip}
                  status={u.status === 'complete' ? 'complete' : 'uploading'}
                  progress={u.progress}
                  statusText={
                    u.status === 'uploading'
                      ? `${u.progress}%`
                      : u.status === 'processing'
                        ? 'Finishing…'
                        : undefined
                  }
                  onCancel={() => cancelUpload(u.id)}
                  onRemove={() => cancelUpload(u.id)}
                />
              </li>
            ))}
          </ul>
        </>
      )}
    </section>
  );
}
